import React, { useEffect, useRef, useState } from "react";
import { DEMO_CAL_URL, mountDemoCalendar } from "./demo-calendar";
import {
  campaignAttribution,
  createDemoMeasurement,
  readConsent,
} from "./demo-measurement";
import { recordLandingBooking } from "./public-analytics";
import "./demo-booking.css";

export default function DemoBooking() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState("idle");
  const [consent, setConsent] = useState(() => readConsent());
  const calendar = useRef(null);
  const measurement = useRef(null);
  if (!measurement.current) measurement.current = createDemoMeasurement();

  useEffect(() => {
    measurement.current.start();
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    setState("loading");
    return mountDemoCalendar(calendar.current, {
      attribution: campaignAttribution(
        window.location.search,
        consent === true,
      ),
      onReady: () =>
        setState((previous) => (previous === "submitted" ? previous : "ready")),
      onError: () =>
        setState((previous) => (previous === "submitted" ? previous : "error")),
      onRequest: (uid) => {
        setState("submitted");
        measurement.current.recordRequest(uid);
        recordLandingBooking(uid);
      },
    });
    // Consent is chosen before opening. A later change closes the calendar to clear attribution.
  }, [open, consent]);

  function updateConsent(allowed) {
    setOpen(false);
    setState("idle");
    measurement.current.setConsent(allowed);
    setConsent(allowed);
  }

  const directUrl = new URL(DEMO_CAL_URL);
  Object.entries(
    campaignAttribution(window.location.search, consent === true),
  ).forEach(([key, value]) => directUrl.searchParams.set(key, value));

  return (
    <div className="demo-booking" aria-labelledby="demo-booking-title">
      <div className="demo-booking-intro">
        <span className="lp-eyebrow">30 minute · Online · În limba română</span>
        <h3 id="demo-booking-title">
          Demonstrație pentru echipa instituției tale
        </h3>
        <p>
          Vedem cum circulă un referat între compartimente, cine îl aprobă și
          cum urmăriți starea documentelor.
        </p>
        <p>
          La programare îți cerem instituția, localitatea, numărul de angajați,
          funcția și modul actual de lucru. Verificăm solicitarea și îți
          comunicăm detaliile comerciale înainte de confirmarea întâlnirii.
        </p>
      </div>

      {!open && (
        <button
          className="lp-btn"
          type="button"
          data-analytics-event="book_demo_click"
          data-analytics-label="demo_calendar"
          onClick={() => setOpen(true)}
        >
          Alege un interval de 30 minute
        </button>
      )}
      <div className="demo-status" role="status" aria-live="polite">
        {open && state === "loading" && (
          <p>Se încarcă intervalele disponibile…</p>
        )}
        {open && state === "error" && (
          <p>
            Calendarul se încarcă mai greu. Poți folosi linkul direct de mai jos
            sau ne poți contacta prin telefon ori e-mail.
          </p>
        )}
        {state === "submitted" && (
          <p className="demo-success">
            Solicitarea de programare a fost înregistrată. Întâlnirea va avea
            loc după verificarea și confirmarea noastră.
          </p>
        )}
      </div>
      {open && (
        <div
          ref={calendar}
          className="demo-calendar"
          aria-label="Calendar pentru demonstrația platformei de achiziții"
        />
      )}
      {open && (
        <p>
          <a
            href={directUrl.toString()}
            target="_blank"
            rel="noopener noreferrer"
          >
            Deschide calendarul într-o pagină separată
          </a>
        </p>
      )}

      <details className="demo-privacy">
        <summary>Datele programării și măsurarea reclamelor</summary>
        <p>
          Datele completate în calendar sunt prelucrate prin Cal.com pentru
          organizarea demonstrației și răspunsul la solicitare. Nu includem
          numele, e-mailul sau răspunsurile tale în evenimentele trimise către
          Google Ads.
        </p>
        <p>
          Măsurarea publicitară este opțională. Dacă o accepți, putem asocia
          sursa reclamei cu programarea și folosi cookie-uri Google Ads pentru
          măsurarea solicitărilor. Nu activăm remarketingul. Poți programa un
          demo și fără această măsurare.
        </p>
        <p>
          Preferința este memorată în browser până la 180 de zile și poate fi
          schimbată aici. Schimbarea preferinței închide calendarul; datele încă
          netrimise vor trebui completate din nou.
        </p>
        <p>
          Pentru întrebări despre datele tale:{" "}
          <a href="mailto:lucian@partsfindersoftware.com">
            lucian@partsfindersoftware.com
          </a>
          . Consultă și politicile{" "}
          <a
            href="https://cal.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cal.com
          </a>{" "}
          și{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google
          </a>
          .
        </p>
      </details>
      <div
        className="demo-consent"
        aria-label="Preferință pentru măsurarea publicitară"
      >
        <p>
          {consent === true
            ? "Măsurarea reclamelor este acceptată."
            : "Măsurarea reclamelor este oprită."}
        </p>
        <div className="demo-consent-actions">
          <button
            type="button"
            aria-pressed={consent === false}
            onClick={() => updateConsent(false)}
          >
            Fără măsurare
          </button>
          <button
            type="button"
            aria-pressed={consent === true}
            onClick={() => updateConsent(true)}
          >
            Accept măsurarea
          </button>
        </div>
      </div>
    </div>
  );
}
